import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import globals from 'scoreboard/components/globals'
import BaseRouter from 'scoreboard/components/base-router'
import appDisplay from 'scoreboard/components/app-display'
import Report, { defaultPeriodSettings } from './report'
import Nav from './reports-nav'
import ReportsOverview from './reports-overview'
import ReportsEdit from './reports-edit'
import reportHeader from './report-header'
import ReportsFolderOverview from 'scoreboard/reports/reports-folder-overview'
import FolderForm from 'scoreboard/components/folder-form'
import viewEditTabs from 'scoreboard/components/view-edit-tabs.hbs'
import notImplemented from 'scoreboard/components/not-implemented.hbs'
import EmptyState from 'scoreboard/components/empty-state'
import metadata from 'scoreboard/components/metadata'
import conductor from 'scoreboard/components/conductor'
import constants from 'scoreboard/components/constants'
import CannotEditCopyView from 'scoreboard/components/designer/cannot-edit-copy-view'
import spectator from 'scoreboard/components/spectator'
import notFound from 'scoreboard/components/not-found.hbs'
import i18n from 'scoreboard/components/i18n'
import exportState from 'scoreboard/components/export/export-state'
import reportConstants from './report-constants'
import getCreateFolderFunction from 'scoreboard/components/get-create-folder-function'
import noPermission from 'scoreboard/components/no-permission.hbs'
import errorHandler from '../components/error-handler'

var ReportsRouter = BaseRouter.extend({

	routes: {
		'reports/export(/)': 'export',
		'organizations/:orgId/reports/new(/)': 'overviewNew',
		'organizations/:orgId/reports/new/edit(/)': 'editNew',
		'reports/generate': 'generateReport',
		'reports(/:id)/new-folder(/)': 'createFolder',
		'reports(/:id)/new(/)': 'overview', // #37756 refreshing unsaved new report
		'reports/new/edit(/)': 'edit',
		'reports/new': 'overviewNew',
		'reports/:id/edit(/)': 'edit',
		'reports/:id(/)': 'overview'	//should be last as its url is the least specific
	},

	Model: Report,
	Nav: Nav,
	section: 'reports',

	initialize: function() {
		BaseRouter.prototype.initialize.apply(this, arguments)
		this.listenTo(Backbone, 'reports:generate', (report) =>{
			this.report = report
		})
	},

	overviewNew: function(orgId) {
		this._route(null, orgId, ReportsFolderOverview, ReportsOverview, false, {results: true})
	},

	editNew: function(orgId) {
		this._route(null, orgId, FolderForm, ReportsEdit, true, {results: false})
	},

	overview: function(id) {
		this._route(id, globals.orgId, ReportsFolderOverview, ReportsOverview, false, {
			results: true,
			templateInfo: true,
			subscriptions: true
		}, { state: this.getState() })
	},

	export: function() {
		let model = new this.Model(exportState.state).toSubtype() //, {silent: true})
		appDisplay.display(model.fetch({}), () => {
			return new ReportsOverview(_.extend({
				model,
				spectating: false
			}, {}))
		})
	},

	/**
	 * Returns the deserialized `state` parameter from the current route's query string,
	 * export state, or null if there isn't one.
	 */
	getState() {
		// Exports of dashboards or bookmarks with unsaved report sort state
		let state = exportState.state

		// In app bookmark or slide with unsaved report sort state
		if (!state) {
			let stateParam

			let url = Backbone.history.fragment
			let queryStart = url.indexOf('?')

			if (queryStart != -1) {
				let query = url.substring(queryStart+1)
				let params = new URLSearchParams(query)
				stateParam = params.get('state')
			}

			if (!_.isNil(stateParam)) {
				state = JSON.parse(stateParam)
				if (_.isEmpty(state)) {
					return null
				}
			}
		}
		return state
	},

	edit: function(id) {
		let options = _.defaults({
			results: false,
			templateInfo: true,
			subscriptions: true,
			edit: true
		}, reportConstants.EDIT_FETCH_OPTIONS)
		this._route(id, globals.orgId, FolderForm, ReportsEdit, true, options)
	},

	/**
	 * @param {Object} params parameters that should be sent with the fetch XHR
	 * @param {Object} viewOptions options that should be passed to the View constructor
	 */
	_route: function(id, orgId, folderView, reportView, navEditable, params, viewOptions) {
		//reports can toggle back and forth between overview/edit without saving
		//the report, no need to hit server in these situations
		this._currentModel = appDisplay.displayView() && appDisplay.displayView().model

		let useCachedReport
		if (conductor.path().indexOf('/new') != -1) {
			//#45783 ensure when switching between new reports, we use the cached report
			useCachedReport = true
		} else {
			//detect whether we're still looking at the same report, if not load
			//the report by the id in the url.
			useCachedReport = _.get(this._currentModel, 'id', null) == id
		}

		// the 'new' occurs when creating a new report from an org that does not
		// have a previous report already persisted
		if (this._currentModel && this._currentModel.domainType === 'report' && useCachedReport) {
			this._routeCached(folderView, reportView, navEditable, params, viewOptions)
		} else if (!_.isNil(id)) {
			this._routeLoad(id, folderView, reportView, navEditable, params, viewOptions)
		} else {
			//special case when refreshing new reports on empty orgs #9102
			var url = 'organizations/' + orgId + '/' + constants.SECTION_REPORTS
			conductor.navigate(url, {trigger: true})
		}
	},

	/**
	 * @param {Object} params parameters that should be sent with the fetch XHR
	 * @param {Object} viewOptions options that should be passed to the View constructor
	 */
	_routeCached: function(folderView, reportView, navEditable, params, viewOptions) {
		//#9456 ensure we're using the most recent global period
		this._currentModel.set('relativePeriodId', globals.periodId)
		_.set(this._currentModel, 'editing', navEditable)

		const prepViewOptions = {
			model: this._currentModel,
			folderView, reportView, navEditable, viewOptions
		}

		let modelPromise = Promise.resolve(this._currentModel)
		//the new report must always refetch when switching the view due to their complexity
		if (!this._currentModel.designer && (this._currentModel.stale === false || !params.results)) {
			//not showing results, or already loaded, go ahead and show
			appDisplay.display(this._prepView(prepViewOptions))
		} else {
			//we're changing the view, do any pre-removal logic
			_.invoke(appDisplay.displayView(), 'prepareRemove')
			//need to fetch the results and show
			modelPromise = this._currentModel.fetch()
			if (this._currentModel.designer) {
				//#46358 on error appDisplay.display simply redisplayed the current
				//view, but in this case the designer will be robust enough to
				//render the intended view, so shim the promise to resolve successfully
				//so appDisplay.display can display thew right view
				let fetchPromise = modelPromise
				modelPromise = $.Deferred()
				fetchPromise.always((arg) => {
					modelPromise.resolve(arg)
				})
			}
			appDisplay.display(modelPromise, () => this._prepView(prepViewOptions))
		}

		// id will be undefined for new unsaved report
		var id = this._currentModel.id || this._currentModel.prevReportId
		this._navForModel(modelPromise, id, navEditable, {keepTransient: true})
	},

	/**
	 * Called when loading a new report (id differs from the one currently loaded
	 * or no report currently loaded)
	 *
	 * @param {Object} params parameters that should be sent with the fetch XHR
	 * @param {Object} viewOptions options that should be passed to the View constructor
	 */
	_routeLoad: function(id, folderView, reportView, navEditable, params, viewOptions) {
		//#45512 if the export state was given, use that instead of what is returned by the server
		if (!_.isNil(_.get(viewOptions, 'state', null))) {
			params.state = viewOptions.state
		}

		let errResponseToForm = (xhr) => {
			let form = _.get(xhr.responseJSON, 'payload')
			if (form) {
				return new Report(form).toSubtype({xhrError: xhr})
			}
			return null
		}

		let error = (obj, resp, opt) => {
			//#46358 if not designer, show generic error, otherwise continue on to the view
			if (!_.get(errResponseToForm(resp), 'designer', false)) {
				errorHandler(resp)
			}
		}

		// if there is a ui state (in a briefing), we want to look at the
		// current period and not the period that was selected when we "saved"
		// the report
		if (!_.isNil(params.state) && !_.isNil(globals.periodId)) {
			_.extend(params.state, defaultPeriodSettings())
		}

		//fetch & display the report denoted by id
		const loadPromise = this.load(id, params, null, error)
		const displayPromise = $.Deferred()
		loadPromise.then(
			(successArgs) => { displayPromise.resolve(successArgs)},
			(xhr) => {
				let form = errResponseToForm(xhr)
				let isDesigner = _.get(form, 'designer', false)
				//#46358 only the designer reports have support for error states
				isDesigner ? displayPromise.resolve(form) : displayPromise.reject(xhr)
		})

		appDisplay.display(displayPromise, model => {
			//the report model is polymorphic, ensure the correct model is cached
			this._currentModel = model
			return this._prepView({
				model, folderView, reportView, navEditable, viewOptions
			})
		})
		this._navForModel(displayPromise, id, navEditable, params)
	},

	_isDesignerError: function(xhr) {
		//#46358 handle the scenario where the form hasn't been loaded yet
		let form = _.get(xhr.responseJSON, 'payload')
		if (form) {
			return new Report(form).toSubtype().designer
		}
		return false
	},

	_navForModel: function (modelPromise, id, edit, params) {
		if (id) {
			const navOptions = _.defaults({ id, edit }, params)
			this.navForModel(navOptions, modelPromise)
		}
	},

	/**
	 * @param {Object} options.viewOptions options that should be passed to the View constructor
	 */
	_prepView: function(options) {
		const {
			model,
			folderView,
			reportView,
			navEditable,
			viewOptions
		} = options

		//in some instances the model passed in is the generic report model and
		//the specific report subtype (which is what we need) is held in _model.
		//Note this can also be a model of a report folder.
		var m = model && model._model ? model._model : model

		let subtype = m.get('subtype')
		if (subtype == 'unsupported-replicated-report') {
			let body = noPermission({message: i18n.get('common.replication.datasetReportFromLowSide'),
				hideContactAdminMessage: true})
			return new EmptyState({ body, model: m })
		}

		if (navEditable && !m.isEditable()) {
			let body = this._notEditableBody(m)
			return new EmptyState({ body, model: m })
		}

		if (m.get('templateId') && navEditable) {
			return new CannotEditCopyView({
				model: m,
				section: 'reports',
				headerFn: reportHeader.generate
			})
		}

		var view = new folderView({ model: m, tabs: viewEditTabs, overviewTabCode: 'common.view' })
		if (m.get('type') !== 'folder') {
			//if no subtype, report not yet implemented
			view = new EmptyState({
				body: notImplemented(metadata.reports)
			})

			if (subtype) {
				//spectating is when the report is being viewed outside the report section
				//e.g. a bookamrk or briefing
				view = new reportView(_.extend({
					model: m,
					spectating: spectator.spectates(conductor.section())
				}, viewOptions))
			}
		}

		this.setBrieferView(view)

		return view
	},

	overviewWithModel: function(model) {
		return this._prepView({
			model,
			folderView: ReportsFolderOverview,
			reportView: ReportsOverview,
			navEditable: false })
	},

	createFolder: getCreateFolderFunction(true, { header: 'report.reports' }),

	preventGuard: function(href) {
		//prevent guard if switching between various views on same report
		return conductor.section(href) == conductor.section()
			&& conductor.id(href) == conductor.id(href)
	},

	generateReport: function() {
		let report = this.report
		if (_.isNil(report)) {
			appDisplay.display(notFound())
			return
		}
		let orgId = report.get('organizationId') || globals.orgId

		//update the url, #16718 replace so we don't lose our way back
		conductor.navigate('organizations/'+orgId+'/reports/new', {replace: true})
		this._navToReport(report)
	},

	/* This internal function is for navigation to reports that have been generated from other sections.
	 * In these cases in addition to loading the display, we also update the nav pane reflecting the
	 * reports in the org
	 */
	_navToReport: function(model) {
		let orgId =_.get(model, 'attributes.organzationId', globals.orgId)
		let domain = _.get(model, 'domainType', null)

		//we need the org to do anything, so if not available sorry
		//also if this model doesn't represent a report, then this is a error so show not found
		if (!orgId || domain != 'report') {
			appDisplay.display(notFound())
			return
		}

		this._currentModel = model
		this._routeCached(ReportsFolderOverview, ReportsOverview, false, {results: true})
		this.nav({ orgId: globals.orgId }).then(navView => {
			navView.addTransient(null, this._currentModel.attributes)
		})
	}

})

export default ReportsRouter

ReportsRouter.__filename__ = 'reports/reports-router.js'
